/**
 * Turns the id a record is stored under into the token that stands for it in
 * the URL, and back again.
 *
 * ## What this is for, and what it is not
 *
 * A URL is seen by people who are not looking for it. It sits in the address
 * bar during a screen-share, goes into any screenshot of the window, stays in
 * browser history after a sign-out, and travels whole when someone pastes a
 * link into a chat to ask a colleague about it. A raw `public_id` in that
 * position is readable by all of them.
 *
 * So the id is enciphered on the way into a path and deciphered on the way
 * out. What that buys is that a URL no longer *reads* as a record — it stops
 * the shoulder, the screenshot and the pasted link, which is the whole of the
 * threat it is meant for.
 *
 * What it does not buy is secrecy from anyone who tries. The key below is
 * compiled into the bundle that ships to the browser, so it is readable by
 * whoever opens the sources tab, and a token can be turned back into an id by
 * anyone willing to do that. This is obfuscation with a key, and calling it
 * anything stronger would be a lie that someone later builds on. AES would not
 * change it: the weakness is the shipped key, not the cipher.
 *
 * **Nothing may rely on a token being unreadable.** Access control is the
 * server's, and stays there — a request from a browser with no admin session
 * is refused whether the id arrived enciphered or not. Treat a decoded id as
 * exactly as untrusted as one typed into the bar.
 *
 * There is a second reason it earns its place in *this* app that it does not
 * in the dietitian one: a user id here is the API's own sequential key, so a
 * raw one in the bar is a number anyone can add one to. The token is not what
 * stops them — the server is — but it removes the invitation.
 *
 * ## Why this shape
 *
 * Deterministic: one id always makes the same token. A bookmark keeps working,
 * a link is the same link tomorrow, and Back does not fill up with several
 * spellings of one screen. A random nonce per token would cost all of that and
 * add no secrecy that a shipped key has not already given away.
 *
 * Synchronous: `UserDetailPage` reads its param during render. `crypto.subtle`
 * is promise-only, so using it would make the id arrive a tick after the
 * screen does, and every consumer would have to grow a loading state for a
 * string it already has. Hence a stream cipher written out here rather than a
 * Web Crypto call.
 */

/**
 * Override per environment with `REACT_APP_ROUTE_ID_SECRET`. Changing it
 * invalidates every URL already in someone's bookmarks or history — they stop
 * decoding and land on "not found" — so treat a change as a breaking one.
 *
 * It is not a secret in any real sense; see the note above. It is the value
 * the keystream is derived from, and it lives in the bundle.
 */
const SECRET =
  process.env.REACT_APP_ROUTE_ID_SECRET || "naurist.admin.route-id.v1";

/**
 * Bytes of the id's hash carried at the front of the token. They are what the
 * keystream is derived from, and what a decode is checked against.
 */
const HEADER_BYTES = 4;

/**
 * FNV-1a, 32-bit, finished with murmur3's avalanche step.
 *
 * The finisher is not decoration. FNV's multiply only carries a changed bit
 * upwards, so two ids differing in the last character leave most of the high
 * bits alone — and this hash seeds the keystream, where that would show up as
 * two tokens sharing everything but their tail. Avalanching first means one
 * changed character moves every bit of the result, and so every byte of the
 * token.
 */
const hash32 = (input: string): number => {
  let hash = 0x811c9dc5;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    // Multiplication that stays 32-bit, which `*` would not.
    hash = Math.imul(hash, 0x01000193);
  }

  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;

  return hash >>> 0;
};

/**
 * `length` bytes of a deterministic pad, from mulberry32. The same seed always
 * gives the same bytes, which is what makes a token stable.
 */
const keystream = (seed: number, length: number): Uint8Array => {
  const out = new Uint8Array(length);
  let state = seed;

  for (let index = 0; index < length; index += 1) {
    state = (state + 0x6d2b79f5) | 0;
    let mixed = Math.imul(state ^ (state >>> 15), 1 | state);
    mixed = (mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed)) ^ mixed;
    out[index] = ((mixed ^ (mixed >>> 14)) >>> 0) & 0xff;
  }

  return out;
};

/** Base64 the URL leaves alone: no `+`, no `/`, and no `=` to be escaped. */
const toBase64Url = (bytes: Uint8Array): string => {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/** The reverse. Returns null rather than throwing on anything malformed. */
const fromBase64Url = (token: string): Uint8Array | null => {
  // A token only ever holds the alphabet below. Rejecting here keeps `atob`
  // from being handed something a browser might be lenient about.
  if (!/^[A-Za-z0-9\-_]+$/.test(token)) return null;

  const padded = token
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(token.length / 4) * 4, "=");

  try {
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  } catch {
    return null;
  }
};

const utf8 = new TextEncoder();
// `fatal` so bytes that are not valid UTF-8 throw instead of quietly becoming
// replacement characters — a wrong id is worth catching, not papering over.
const fromUtf8 = new TextDecoder("utf-8", { fatal: true });

/**
 * The token for a stored id. An empty id gives an empty token, so a caller
 * that has not loaded its record yet builds a path that resolves to the
 * screen's own "not found" rather than to a token standing for nothing.
 */
export const encodeRouteId = (id: string): string => {
  if (!id) return "";

  const body = utf8.encode(id);
  const check = hash32(id);

  // The keystream comes from the id's own hash as well as the key, which is
  // what keeps two near-identical ids from making two near-identical tokens.
  // Were the pad the key's alone, it would be one fixed pad reused for every
  // id, and `seed-plan-1` and `seed-plan-2` would differ in exactly the one
  // character they differ in — enough to read the shape of an id off a URL and
  // to walk to the next by hand. Hashing the id in first means one changed
  // character reseeds the whole pad.
  const pad = keystream((hash32(SECRET) ^ check) >>> 0, body.length);

  // The hash travels in the token, since the decode needs it to rebuild the
  // pad before it has the id. Masked with a second key-derived value so the
  // header is not a plain fingerprint of the id — equal headers would still
  // say "same client" to anyone comparing two URLs.
  const masked = (check ^ hash32(`${SECRET}:header`)) >>> 0;

  const out = new Uint8Array(HEADER_BYTES + body.length);
  out[0] = (masked >>> 24) & 0xff;
  out[1] = (masked >>> 16) & 0xff;
  out[2] = (masked >>> 8) & 0xff;
  out[3] = masked & 0xff;
  body.forEach((byte, index) => {
    out[HEADER_BYTES + index] = byte ^ pad[index];
  });

  return toBase64Url(out);
};

/**
 * The id a token stands for, or `""` if it does not stand for one — a token
 * that was truncated in a chat, typed wrong, or made under a different
 * `SECRET`. Both detail screens treat an empty id as a missing record, so a
 * bad URL lands on "not found" instead of on an error.
 */
export const decodeRouteId = (token: string): string => {
  if (!token) return "";

  const bytes = fromBase64Url(token);
  if (!bytes || bytes.length <= HEADER_BYTES) return "";

  const masked =
    ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0;
  const check = (masked ^ hash32(`${SECRET}:header`)) >>> 0;

  const cipher = bytes.subarray(HEADER_BYTES);
  const pad = keystream((hash32(SECRET) ^ check) >>> 0, cipher.length);
  const body = cipher.map((byte, index) => byte ^ pad[index]);

  let id: string;
  try {
    id = fromUtf8.decode(body);
  } catch {
    return "";
  }

  // The whole 32-bit hash has to agree, so a token altered anywhere — header
  // or body — fails here rather than decoding into some other id.
  return hash32(id) === check ? id : "";
};
